import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSpinner, faFilePdf, faFileImage, faUser, faMoneyBillWave, faRoute, faDownload, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Api from '../../Api';
import { useAuth } from '../../contexts/AuthContext';

const STATUS_META = {
    paid: { label: 'Payé', cls: 'bg-green-100 text-green-800 border-green-200' },
    unpaid: { label: 'Non payé', cls: 'bg-red-100 text-red-800 border-red-200' },
    rejected: { label: 'Annulé', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
    insufficient: { label: 'Insuffisant', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
    excluded: { label: 'Exclu', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
    paid_via_carryover: { label: 'Payé (report)', cls: 'bg-green-100 text-green-800 border-green-200' },
    pending: { label: 'En attente', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

const ADV_STATUS_META = {
    approved: { label: 'Approuvée', cls: 'bg-blue-100 text-blue-700' },
    paid: { label: 'Payée', cls: 'bg-green-100 text-green-700' },
    reported: { label: 'Reportée', cls: 'bg-amber-100 text-amber-700' },
    rejected: { label: 'Rejetée', cls: 'bg-red-100 text-red-700' },
    cancelled: { label: 'Annulée', cls: 'bg-gray-100 text-gray-600' },
    pending: { label: 'En attente', cls: 'bg-gray-100 text-gray-600' },
};

const fmt = (n) => (n != null ? parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00');

// French labels for adjustment types (raw types shown otherwise).
const ADJ_LABELS = {
    bonus: 'Bonus',
    penalty: 'Pénalité',
    deleted_survey_deduction: 'Déduction',
    deleted_paid_submission_clawback: 'Récupération (soumission supprimée)',
    advance_frais_deplacement: 'Frais de déplacement',
    survey_exclusion: 'Exclusion enquête',
    carryover: 'Report',
    pending_deduction_recovery: 'Récupération',
};
const adjLabel = (type) => ADJ_LABELS[type] || type;
// A survey_exclusion with kind 'retenue' withholds only a percentage of the survey (F2F)
const adjLabelOf = (adj) => (adj?.kind === 'retenue'
    ? `Retenue ${Number(adj.percentage || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 })} %${adj.survey_title ? ` (${adj.survey_title})` : ''}`
    : adjLabel(adj?.type));

const PaymentLineDetail = () => {
    const { id, lineId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const res = await Api.get(`/payments/${id}/lines/${lineId}/detail`);
                if (active) {
                    if (res.data.success) setData(res.data.data);
                    else setError(res.data.message || 'Erreur lors du chargement');
                }
            } catch (e) {
                if (active) setError(e.response?.data?.message || 'Erreur lors du chargement du détail');
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [id, lineId]);

    const handleDownload = async (file) => {
        try {
            const res = await Api.get(`/payments/proof-files/${file.id}/download`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', file.original_filename || 'proof-file');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Download error:', e);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-3xl text-red-600" />
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gray-50 p-6">
                <button onClick={() => navigate(`/payments/${id}`)} className="text-sm text-blue-600 hover:underline mb-4">
                    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />Retour au lot
                </button>
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4">{error || 'Introuvable'}</div>
            </div>
        );
    }

    const statusMeta = STATUS_META[data.payment_status] || STATUS_META.pending;

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3">
                            <div className="w-11 h-11 rounded-full bg-gray-100 flex items-center justify-center">
                                <FontAwesomeIcon icon={faUser} className="text-gray-500" />
                            </div>
                            <div>
                                <h1 className="text-lg font-bold text-gray-900">{data.person_name}</h1>
                                <div className="text-xs text-gray-500">
                                    {data.person_type} {data.person_ref ? `· Ref: ${data.person_ref}` : ''} · Ligne #{data.id}
                                </div>
                            </div>
                        </div>
                        <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${statusMeta.cls}`}>
                            {statusMeta.label}
                        </span>
                    </div>

                    {/* Amount + method */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
                        <div>
                            <div className="text-xs text-gray-500">Total net</div>
                            <div className="text-base font-semibold text-gray-900">{fmt(data.net)} MAD</div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Méthode</div>
                            <div className="text-sm text-gray-900">
                                {data.payment_method_type === 'bank' ? `Banque${data.bank_name ? ` · ${data.bank_name}` : ''}` : `Transfert${data.transfer_service ? ` · ${data.transfer_service}` : ''}`}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-gray-500">Section</div>
                            <div className="text-sm text-gray-900 uppercase">{data.section || '—'}</div>
                        </div>
                    </div>

                    {/* Confirmation info */}
                    {(data.confirmed_by || data.confirmed_at) && (
                        <div className="mt-4 pt-4 border-t border-gray-100 text-xs text-gray-500">
                            Confirmé {data.confirmed_by ? `par ${data.confirmed_by}` : ''} {data.confirmed_at ? `le ${data.confirmed_at}` : ''}
                        </div>
                    )}
                </div>

                {/* Cancellation / notes highlight */}
                {data.payment_status === 'rejected' && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5 mb-5">
                        <div className="text-sm font-semibold text-yellow-800 mb-1">Ligne annulée</div>
                        <div className="text-sm text-yellow-900">{data.payment_notes || 'Aucun motif renseigné.'}</div>
                    </div>
                )}

                {/* Linked advances */}
                {data.advances && data.advances.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
                        <h2 className="text-sm font-bold text-gray-800 mb-3">
                            <FontAwesomeIcon icon={faRoute} className="mr-2 text-gray-400" />
                            Avances de frais de déplacement
                        </h2>
                        <div className="space-y-3">
                            {data.advances.map((adv, i) => {
                                const am = ADV_STATUS_META[adv.status] || ADV_STATUS_META.pending;
                                return (
                                    <div key={i} className="border border-gray-100 rounded-lg p-3">
                                        <div className="flex items-center justify-between gap-3 flex-wrap">
                                            <div className="text-sm font-semibold text-gray-900">{fmt(adv.amount)} MAD</div>
                                            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${am.cls}`}>{am.label}</span>
                                        </div>
                                        <div className="text-xs text-gray-500 mt-1">
                                            {adv.project_survey_title || `Projet #${adv.project_id}`}
                                            {adv.planned_date ? ` · prévu le ${String(adv.planned_date).split('T')[0]}` : ''}
                                        </div>
                                        {adv.status === 'rejected' && (
                                            <div className="text-xs text-red-700 mt-2 bg-red-50 rounded p-2">
                                                <span className="font-medium">Rejetée :</span> {adv.rejection_reason || '—'}
                                                {adv.rejected_by ? ` (par ${adv.rejected_by}${adv.rejected_at ? `, ${adv.rejected_at}` : ''})` : ''}
                                            </div>
                                        )}
                                        {adv.project_id && user?.role !== 'resp_admin' && (
                                            <button
                                                onClick={() => navigate(`/f2f/frais-deplacement/${adv.project_id}`)}
                                                className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline mt-2"
                                            >
                                                Ouvrir le projet de frais
                                                <FontAwesomeIcon icon={faArrowRight} className="text-[10px]" />
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Adjustments */}
                {data.adjustments && data.adjustments.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
                        <h2 className="text-sm font-bold text-gray-800 mb-3">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2 text-gray-400" />
                            {data.batch_creation_mode === 'express' ? 'Avances' : 'Ajustements'}
                        </h2>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-200">
                                        <th className="py-2 pr-3">Type</th>
                                        <th className="py-2 px-3">Montant</th>
                                        <th className="py-2 px-3">Raison</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.adjustments.map((adj, i) => (
                                        <tr key={i}>
                                            <td className="py-2 pr-3 text-gray-700">{adjLabelOf(adj)}</td>
                                            <td className="py-2 px-3 text-gray-900 whitespace-nowrap">{fmt(adj.amount)} MAD</td>
                                            <td className="py-2 px-3 text-gray-500">{adj.reason || '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Proof files */}
                {data.proof_files && data.proof_files.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
                        <h2 className="text-sm font-bold text-gray-800 mb-3">Fichiers de preuve</h2>
                        <div className="space-y-2">
                            {data.proof_files.map((f) => (
                                <button
                                    key={f.id}
                                    onClick={() => handleDownload(f)}
                                    title="Télécharger le fichier"
                                    className="w-full flex items-center gap-3 text-sm text-left rounded-lg px-2 py-1.5 hover:bg-gray-50 transition-colors group"
                                >
                                    <FontAwesomeIcon icon={f.is_pdf ? faFilePdf : faFileImage} className="text-gray-400 group-hover:text-red-500" />
                                    <span className="text-blue-600 group-hover:text-blue-800 group-hover:underline">{f.original_filename}</span>
                                    <span className="text-xs text-gray-400">{f.file_size} · {f.uploaded_at}{f.uploaded_by ? ` · ${f.uploaded_by}` : ''}</span>
                                    <FontAwesomeIcon icon={faDownload} className="text-gray-300 group-hover:text-gray-500 ml-auto" />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* Modification history */}
                {data.modification_history && data.modification_history.length > 0 && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                        <h2 className="text-sm font-bold text-gray-800 mb-3">Historique des modifications</h2>
                        <div className="space-y-2">
                            {data.modification_history.map((h, i) => (
                                <div key={i} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-3">
                                    <span className="font-medium">{h.from_status} → {h.to_status}</span> · {h.by} · {h.at}
                                    {h.reason ? <div className="text-gray-500 italic">{h.reason}</div> : null}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentLineDetail;
